import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Brain, ShieldCheck, Camera, User, CheckCircle2 } from "lucide-react";
import { getTheme, fonts } from "../theme";
import Navbar from "../components/Navbar";
import TrustDial from "../components/TrustDial";
import SocialLinks from "../components/SocialLinks";
import BreakdownGrid from "../components/BreakdownGrid";
import { Button } from "../components/UI";
import { EMPLOYER_CRITERIA } from "../criteria";
import PrivateRatingsOnFile from "../components/PrivateRatingsOnFile";
import api, { fileUrl } from "../api";

export default function EmployeeDashboard({ user: propUser, onLogout, themeMode = "dark", setThemeMode }) {
  const theme = getTheme("employee", themeMode);

  const { id: paramId } = useParams();

  const user = propUser || (() => {
    try {
      const s = sessionStorage.getItem("wibil_user") || localStorage.getItem("wibil_user");
      return s && s !== "undefined" ? JSON.parse(s) : null;
    } catch (e) { return null; }
  })();

  const targetEmployeeId = paramId || user?.id || null;

  const [data, setData] = useState(null);
  const [banking, setBanking] = useState(null);
  const [error, setError] = useState(null);
  const [identityPanel, setIdentityPanel] = useState(null);
  const [identityBusy, setIdentityBusy] = useState(false);
  const [identityError, setIdentityError] = useState("");
  const [identityOk, setIdentityOk] = useState("");
  const [aadhaarFrontName, setAadhaarFrontName] = useState("");
  const [aadhaarBackName, setAadhaarBackName] = useState("");
  const [aadhaarPreview, setAadhaarPreview] = useState("");
  const [uanDraft, setUanDraft] = useState("");
  const [photoPreview, setPhotoPreview] = useState("");
  const photoInputRef = useRef(null);

  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (!targetEmployeeId) return;
    setError(null);
    const isOwn = !paramId || String(paramId) === String(user?.id);
    try {
      const res = await api.get(`/employees/${targetEmployeeId}`);
      setData(res);
    } catch (e) {
      console.error(e);
      setData({
        profile: {
          id: targetEmployeeId,
          name: user?.name || "Verified Professional",
          email: user?.email || "employee@example.com",
          role: "employee",
          title: user?.title || "Software Engineer",
          current_company: user?.current_company || "GoldPE Capital",
          strengths: user?.strengths || "React, Node.js, MS SQL",
          profile_completeness_pct: 100,
          search_appearance_score: 98
        },
        score: { trust_score: 300, rating_count: 0 },
        ratings: [],
        workplace_ratings: [],
        verifications: []
      });
    }

    if (!isOwn) {
      setBanking(null);
      return;
    }

    try {
      const bankData = await api.get(`/banking/eligibility/${targetEmployeeId}`);
      setBanking(bankData);
    } catch (e) { console.error(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetEmployeeId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const uan = data?.profile?.uan || data?.uan;
    if (uan) setUanDraft(String(uan));
  }, [data]);

  if (error && !data) {
    return (
      <div style={{ minHeight: "100vh", background: theme.bg, color: theme.text, fontFamily: fonts.body }}>
        <Navbar theme={theme} userName={user?.name} onLogout={onLogout} themeMode={themeMode} setThemeMode={setThemeMode} />
        <div className="container py-5 text-center">
          <div className="alert alert-danger d-inline-block text-start p-4 rounded-4 shadow-sm" style={{ maxWidth: 500 }}>
            <h5 className="fw-bold mb-2">Unable to Load Dashboard</h5>
            <p className="small mb-3">{error}</p>
            <div className="d-flex gap-2">
              <button className="btn btn-primary btn-sm" onClick={load}>Retry</button>
              <button className="btn btn-outline-secondary btn-sm" onClick={onLogout}>Sign Out</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return <div style={{ background: theme.bg, minHeight: "100vh", color: theme.textMute, padding: 40 }}>Loading employee dashboard…</div>;

  const profile = data.profile || data || {};
  const employeeName = (profile?.name && profile.name !== "Verified Professional" && profile.name !== "Verified Candidate")
    ? profile.name
    : (user?.name || "Employee");

  const score = data.score || { trust_score: 300, rating_count: 0 };
  const workplaceRatings = Array.isArray(data.workplace_ratings) ? data.workplace_ratings : [];
  const intelligence = intelligenceFromWorkplaceRatings(workplaceRatings);
  const isLight = theme.key === "light";
  const completenessPct = profile.profile_completeness_pct || 100;
  const isOwnProfile = !paramId || String(paramId) === String(user?.id);

  const applyIdentityProfile = (next) => {
    setData((prev) => ({
      ...(prev || {}),
      profile: { ...((prev && prev.profile) || prev || {}), ...next }
    }));
  };

  const saveIdentity = async (body) => {
    if (!user?.id) throw new Error("Please sign in to update identity details.");
    const res = await api.updateSeekerIdentity(user.id, body);
    if (res?.profile) applyIdentityProfile(res.profile);
    return res;
  };

  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Could not read that file."));
    reader.readAsDataURL(file);
  });

  const toggleIdentityPanel = (panel) => {
    if (panel === "aadhaar" && profile.aadhaar_last4) return;
    if (panel === "uan" && profile.uan) return;
    setIdentityPanel((cur) => (cur === panel ? null : panel));
    setIdentityError("");
    setIdentityOk("");
  };

  const handleAadhaarUpload = async (e, side) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (profile.aadhaar_last4) {
      setIdentityError("Aadhaar eKYC is already verified and cannot be changed.");
      e.target.value = "";
      return;
    }
    if (side === "front") setAadhaarFrontName(file.name);
    else setAadhaarBackName(file.name);
    setIdentityError("");
    setIdentityOk("");
    setIdentityBusy(true);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const contentType = /png/i.test(file.type) ? "PNG" : "JPEG";
      const res = await api.verifyAadhaarQr(dataUrl, contentType, employeeName, { strict: true });
      const fields = res?.data?.fields || res?.fields || {};
      const last4 = String(fields.aadhaar_last4 || "").replace(/\D/g, "").slice(-4);
      if (last4.length !== 4) {
        throw new Error("Aadhaar QR did not return last 4 digits. Upload a clear image of the QR side.");
      }
      if (res?.data?.photo?.base64) {
        setAadhaarPreview(`data:${res.data.photo.mime_type || "image/jpeg"};base64,${res.data.photo.base64}`);
      }
      await saveIdentity({ aadhaar_last4: last4 });
      setIdentityOk("Aadhaar eKYC verified.");
      setIdentityPanel(null);
    } catch (err) {
      setIdentityError(err.message || "Aadhaar verification failed.");
    } finally {
      setIdentityBusy(false);
      e.target.value = "";
    }
  };

  const handleUanSave = async () => {
    if (profile.uan) {
      setIdentityError("EPFO / UAN is already linked and cannot be changed.");
      return;
    }
    const uan = String(uanDraft || "").replace(/\s+/g, "").toUpperCase();
    if (uan.length < 8) {
      setIdentityError("Enter a valid UAN / EPF number (at least 8 characters).");
      return;
    }
    setIdentityBusy(true);
    setIdentityError("");
    setIdentityOk("");
    try {
      await api.post("/verify/epf", { epfNo: uan });
      await saveIdentity({ uan });
      setIdentityOk("EPFO / UAN verified.");
      setIdentityPanel(null);
    } catch (err) {
      setIdentityError(err.message || "Could not link UAN.");
    } finally {
      setIdentityBusy(false);
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const stored = String(profile.profile_photo || profile.profilePhoto || "").trim();
    if (stored || photoPreview) {
      setIdentityError("Profile photo is already set and cannot be changed.");
      e.target.value = "";
      return;
    }
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      setIdentityError("Please upload a JPG, PNG, or WebP photo.");
      return;
    }
    setIdentityBusy(true);
    setIdentityError("");
    setIdentityOk("");
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setPhotoPreview(dataUrl);
      await saveIdentity({ profilePhoto: dataUrl });
      setIdentityOk("Profile photo saved.");
    } catch (err) {
      setIdentityError(err.message || "Could not save profile photo.");
      setPhotoPreview("");
    } finally {
      setIdentityBusy(false);
      e.target.value = "";
    }
  };

  const storedPhoto = String(profile.profile_photo || profile.profilePhoto || "").trim();
  const photoSrc = photoPreview
    || (storedPhoto.startsWith("data:") || storedPhoto.startsWith("http") ? storedPhoto : fileUrl(storedPhoto))
    || "";
  const photoLocked = Boolean(storedPhoto || photoPreview);
  const updateBtnStyle = {
    border: `1px solid ${theme.line}`,
    color: theme.accent,
    background: "transparent",
    fontSize: 11,
    fontWeight: 700,
    padding: "2px 10px"
  };
  const updateActionBtnStyle = {
    background: "#0D6EFD",
    color: "#FFFFFF",
    border: "1px solid #0D6EFD",
    fontSize: 11,
    fontWeight: 700,
    padding: "4px 12px",
    borderRadius: 6
  };

  return (
    <div style={{ minHeight: "100vh", background: theme.bg, color: theme.text, fontFamily: fonts.body, transition: "all 0.2s" }}>
      <Navbar theme={theme} userName={employeeName} onLogout={onLogout} themeMode={themeMode} setThemeMode={setThemeMode} />

      <div className="container py-4 py-md-5">

        {/* Profile Overview Card */}
        <div
          className="card p-4 rounded-4 shadow-sm mb-4"
          style={{
            background: theme.surface,
            border: `1px solid ${theme.line}`,
            color: theme.text,
            boxShadow: isLight ? "0 4px 20px -2px rgba(0, 0, 0, 0.05)" : "none"
          }}
        >
          <div className="row g-4 align-items-center">

            <div className="col-12 col-md-4 col-lg-3 text-center border-end-md" style={{ borderRight: `1px solid ${theme.line}` }}>
              <TrustDial score={score.trust_score} theme={theme} />
              <div className="small mt-2" style={{ color: theme.textMute }}>
                {score.rating_count ?? 0} employer rating{Number(score.rating_count) === 1 ? "" : "s"}
              </div>
            </div>

            <div className="col-12 col-md-8 col-lg-9">
              <div className="d-flex align-items-center gap-3 mb-2">
                {isOwnProfile ? (
                  <button
                    type="button"
                    onClick={() => { if (!photoLocked) photoInputRef.current?.click(); }}
                    title={photoLocked ? "Profile photo is already set" : "Add profile photo"}
                    className="border-0 p-0 position-relative"
                    disabled={identityBusy || !user?.id || photoLocked}
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      background: "transparent",
                      cursor: !photoLocked && user?.id ? "pointer" : "default",
                      flexShrink: 0
                    }}
                  >
                    <span
                      className="d-block w-100 h-100"
                      style={{
                        borderRadius: "50%",
                        overflow: "hidden",
                        background: isLight ? "#F8FAFC" : "rgba(255,255,255,0.04)",
                        border: `2px solid ${theme.line}`
                      }}
                    >
                      {photoSrc ? (
                        <img src={photoSrc} alt={employeeName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <span className="d-flex align-items-center justify-content-center w-100 h-100" style={{ color: theme.textMute }}>
                          <User size={32} />
                        </span>
                      )}
                    </span>
                    {!photoLocked && (
                      <span
                        className="position-absolute d-flex align-items-center justify-content-center"
                        style={{
                          right: 0,
                          bottom: 0,
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          background: theme.accent,
                          color: "#fff",
                          border: "2px solid #fff"
                        }}
                      >
                        <Camera size={12} />
                      </span>
                    )}
                  </button>
                ) : photoSrc ? (
                  <span
                    className="d-block"
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      overflow: "hidden",
                      border: `2px solid ${theme.line}`,
                      flexShrink: 0
                    }}
                  >
                    <img src={photoSrc} alt={employeeName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </span>
                ) : null}
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  hidden
                  onChange={handlePhotoChange}
                />
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <h2 className="h4 fw-bold mb-0" style={{ fontFamily: fonts.display, color: theme.text }}>{employeeName}</h2>
                    <span className="badge bg-primary text-white rounded-pill px-2.5 py-1" style={{ fontSize: 10 }}>
                      {completenessPct}% Synced
                    </span>
                  </div>
                  <div style={{ color: theme.textMute, fontSize: 14 }}>
                    {profile.title}{profile.current_company ? ` at ${profile.current_company}` : ""}
                  </div>
                </div>
              </div>

              <SocialLinks profile={profile} theme={theme} />

              {profile.strengths && (
                <div className="d-flex flex-wrap gap-1 mt-3">
                  {profile.strengths.split(",").map((s, i) => s.trim() && (
                    <span key={i} className="badge rounded-pill px-3 py-1.5" style={{ background: theme.badgeBg, color: theme.badgeText, border: `1px solid ${theme.border}`, fontSize: 11 }}>
                      {s.trim()}
                    </span>
                  ))}
                </div>
              )}

              {isOwnProfile && (
                <div className="p-3 rounded-3 mt-3" style={{ background: isLight ? "#F8FAFC" : "rgba(255,255,255,0.03)", border: `1px solid ${theme.line}` }}>
                  <div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2 small fw-semibold">
                      <ShieldCheck size={16} color={profile.aadhaar_last4 ? "#16A34A" : "#DC2626"} />
                      <span>Aadhaar eKYC:{" "}
                        <strong style={{ color: profile.aadhaar_last4 ? "#16A34A" : "#DC2626" }}>
                          {profile.aadhaar_last4 ? "Verified" : "Not verified"}
                        </strong>
                      </span>
                      {!profile.aadhaar_last4 && (
                        <button type="button" className="btn btn-sm" disabled={identityBusy || !user?.id} onClick={() => toggleIdentityPanel("aadhaar")} style={updateActionBtnStyle}>
                          Update
                        </button>
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-2 small fw-semibold">
                      <CheckCircle2 size={16} color={profile.uan ? "#16A34A" : "#DC2626"} />
                      <span>EPFO / UAN:{" "}
                        <strong style={{ color: profile.uan ? "#16A34A" : "#DC2626" }}>
                          {profile.uan ? "Verified" : "Not linked"}
                        </strong>
                      </span>
                      {!profile.uan && (
                        <button type="button" className="btn btn-sm" disabled={identityBusy || !user?.id} onClick={() => toggleIdentityPanel("uan")} style={updateActionBtnStyle}>
                          Update
                        </button>
                      )}
                    </div>
                  </div>
                  {identityError && <div className="small mt-2 fw-semibold" style={{ color: "#DC2626" }}>{identityError}</div>}
                  {identityOk && <div className="small mt-2 fw-semibold" style={{ color: "#16A34A" }}>{identityOk}</div>}
                  {identityBusy && <div className="small mt-2" style={{ color: theme.textMute }}>Updating identity details…</div>}
                  {identityPanel === "aadhaar" && !profile.aadhaar_last4 && (
                    <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${theme.line}` }}>
                      <div className="small fw-bold mb-2">Upload Aadhaar front and the QR side to verify eKYC</div>
                      <div className="row g-2">
                        <div className="col-12 col-md-6">
                          <label className="btn btn-sm w-100" style={{ ...updateBtnStyle, padding: "8px 10px" }}>
                            {aadhaarFrontName || "Upload front image"}
                            <input type="file" accept="image/jpeg,image/png" hidden disabled={identityBusy} onChange={(e) => handleAadhaarUpload(e, "front")} />
                          </label>
                        </div>
                        <div className="col-12 col-md-6">
                          <label className="btn btn-sm w-100" style={{ ...updateBtnStyle, padding: "8px 10px" }}>
                            {aadhaarBackName || "Upload QR / back image"}
                            <input type="file" accept="image/jpeg,image/png" hidden disabled={identityBusy} onChange={(e) => handleAadhaarUpload(e, "back")} />
                          </label>
                        </div>
                      </div>
                      {aadhaarPreview && (
                        <img src={aadhaarPreview} alt="" className="rounded-circle mt-2" style={{ width: 48, height: 48, objectFit: "cover" }} />
                      )}
                    </div>
                  )}
                  {identityPanel === "uan" && !profile.uan && (
                    <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${theme.line}` }}>
                      <div className="small fw-bold mb-2">Enter UAN / EPF number to link EPFO</div>
                      <div className="d-flex flex-wrap gap-2">
                        <input
                          type="text"
                          className="form-control form-control-sm shadow-none"
                          style={{ maxWidth: 260 }}
                          value={uanDraft}
                          onChange={(e) => setUanDraft(e.target.value.replace(/\s+/g, ""))}
                          placeholder="12-digit UAN"
                          disabled={identityBusy}
                        />
                        <Button theme={theme} onClick={handleUanSave} disabled={identityBusy || String(uanDraft || "").length < 8}>
                          Verify & save
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-3">
                <Button theme={theme} onClick={() => navigate("/rate/employer")}>
                  <Plus size={14} /> Rate an employer
                </Button>
              </div>
            </div>

          </div>
        </div>

        {/* Score Breakdown Section — from workplace (employer) ratings */}
        <div className="mb-5">
          <SectionTitle theme={theme}>Score breakdown</SectionTitle>
          <BreakdownGrid criteria={EMPLOYER_CRITERIA} score={score} theme={theme} />
        </div>

        {/* WIBIL Intelligence Hub — derived from employer ratings */}
        <div className="pt-4 border-top" style={{ borderColor: theme.line }}>
          <SectionTitle theme={theme}>WIBIL Intelligence Hub</SectionTitle>
          <div className="row g-4 mb-5">
            <div className="col-12">
              <div
                className="card h-100 p-4 rounded-4 shadow-sm"
                style={{
                  background: theme.surface,
                  border: `1px solid ${theme.line}`,
                  color: theme.text,
                  boxShadow: isLight ? "0 4px 20px -2px rgba(0, 0, 0, 0.05)" : "none"
                }}
              >
                <h4 className="h6 fw-bold mb-3 d-flex align-items-center gap-2" style={{ color: theme.text }}>
                  <Brain size={18} color={theme.accent} /> AI Predictions & Growth
                </h4>

                {!intelligence ? (
                  <p className="small mb-0" style={{ color: theme.textMute }}>
                    Predictions appear after you submit a private employer rating.
                  </p>
                ) : (
                  <div className="d-flex flex-column gap-3 small">
                    <div>
                      <div style={{ color: theme.textMute }}>Attrition Risk Assessment:</div>
                      <div className="fw-bold text-uppercase fs-6 mt-1" style={{
                        color: intelligence.risk_level === "high" ? (isLight ? "#DC2626" : "#EF4444") : (intelligence.risk_level === "medium" ? (isLight ? "#D97706" : theme.accent) : (isLight ? "#16A34A" : "#22C55E"))
                      }}>
                        {intelligence.risk_level} risk
                      </div>
                      {intelligence.drivers.length > 0 && (
                        <div className="small mt-1" style={{ color: theme.textMute }}>
                          Drivers: {intelligence.drivers.join(", ")}
                        </div>
                      )}
                    </div>

                    <div>
                      <div style={{ color: theme.textMute }}>AI projected workplace score (from your employer ratings):</div>
                      <div className="fw-bold fs-6 mt-1" style={{ color: theme.text }}>
                        {intelligence.predicted_next_performance} / 10 ({intelligence.trend})
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {isOwnProfile === false ? (
          <div>
            <SectionTitle theme={theme}>Private ratings on file</SectionTitle>
            <EmptyNote theme={theme} text="Individual ratings with proof are confidential and are not shown on this profile." />
          </div>
        ) : (
          <PrivateRatingsOnFile
            heading="Private workplace ratings on file"
            ratings={workplaceRatings}
            criteria={EMPLOYER_CRITERIA}
            title="Confidential workplace rating"
            emptyText="No private workplace ratings on file yet."
            theme={theme}
          />
        )}

      </div>
    </div>
  );
}

function SectionTitle({ children, theme }) {
  return <div style={{ fontFamily: fonts.mono, fontSize: 11, letterSpacing: 1.5, color: theme.textMute, marginBottom: 14, textTransform: "uppercase", fontWeight: 700 }}>{children}</div>;
}
function EmptyNote({ text, theme }) {
  return <div style={{ border: `1px dashed ${theme.line}`, borderRadius: 10, padding: 20, color: theme.textMute, fontSize: 13 }}>{text}</div>;
}

function intelligenceFromWorkplaceRatings(list) {
  const ratings = Array.isArray(list) ? list : [];
  if (!ratings.length) return null;
  const keys = ["salary", "environment", "location_score", "growth", "management"];
  const avgs = {};
  keys.forEach((k) => {
    const vals = ratings.map((r) => Number(r[k])).filter((n) => n > 0);
    avgs[k] = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
  });
  const overall = keys.reduce((sum, k) => sum + avgs[k], 0) / keys.length;
  const culture = (avgs.environment + avgs.management + avgs.growth) / 3;
  let risk_level = "low";
  let drivers = ["Strong work culture", "Positive career growth"];
  if (culture < 4.5) {
    risk_level = "high";
    drivers = ["Low work culture score", "Limited career growth"];
  } else if (culture < 7) {
    risk_level = "medium";
    drivers = ["Mixed workplace scores", "Career growth needs attention"];
  }
  if (avgs.salary > 0 && avgs.salary < 5) drivers = [...drivers, "Salary payment concerns"];
  return {
    risk_level,
    drivers,
    predicted_next_performance: Number(overall.toFixed(1)),
    trend: overall >= 7.5 ? "Stable" : overall >= 5 ? "Watch" : "Declining"
  };
}
